import { Search, Users, X } from 'lucide-react';
import { type TableFilters } from '@/types/api';

interface ControlPanelProps {
  filters: TableFilters;
  onFiltersChange: (filters: TableFilters) => void;
  activeGameTab: string;
  onGameTabChange: (tab: string) => void;
  availableGameTabs?: string[];
}

const GAME_TABS = [
  { key: '8ball', label: '8-Ball', emoji: '��', color: 'brand' },
  { key: '9ball', label: '9-Ball', emoji: '9️⃣', color: 'emerald' },
  { key: '10ball', label: '10-Ball', emoji: '🔟', color: 'amber' },
] as const;

export default function ControlPanel({
  filters,
  onFiltersChange,
  activeGameTab,
  onGameTabChange,
  availableGameTabs = ['8ball', '9ball', '10ball'],
}: ControlPanelProps) {
  const handleFilterChange = (key: keyof TableFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-3">
      {/* Game type tabs + controls row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Game tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {GAME_TABS.filter((tab) => availableGameTabs.includes(tab.key)).map((tab) => (
            <button
              key={tab.key}
              onClick={() => onGameTabChange(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeGameTab === tab.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50 active:bg-white/70'
              }`}
            >
              <span>{tab.emoji}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 md:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              className="w-full md:w-56 pl-9 pr-8 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all"
              placeholder="Search players..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            {filters.search && (
              <button
                onClick={() => handleFilterChange('search', '')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Group by team */}
          <button
            onClick={() => handleFilterChange('groupByTeam', !filters.groupByTeam)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
              filters.groupByTeam
                ? 'bg-brand-50 text-brand-700 border-brand-200'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Teams</span>
          </button>
        </div>
      </div>
    </div>
  );
}
